// The file column of the Files tab: what the selected commit did to the file (side by side or in
// one column), or the file as it was at that commit when the commit did not touch it.
import type { FileChange } from '@modelviewer/core';
import { defaultKeymap } from '@codemirror/commands';
import { syntaxHighlighting } from '@codemirror/language';
import { MergeView, unifiedMergeView } from '@codemirror/merge';
import { highlightSelectionMatches, search, searchKeymap } from '@codemirror/search';
import { EditorState, type Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { ArrowLeft, Columns2, History, Loader2, Rows3 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { editorTheme, highlight } from '../lib/editorTheme';
import { cn } from '../lib/cn';
import { languageFor } from '../lib/languages';
import { changeContents, fileContent, showHistory, useFiles } from '../state/files';
import type { FileContent } from './content';
import { STATUS_COLOR, formatSize } from './format';

type DiffLayout = 'split' | 'unified';

const LAYOUT_KEY = 'mv-diff-layout';

function savedLayout(): DiffLayout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'unified' ? 'unified' : 'split';
  } catch {
    return 'split';
  }
}

/** Colors of a diff in the app's palette (the merge view's own theme is drawn over). */
const diffTheme = EditorView.theme({
  '&.cm-merge-a .cm-changedLine, .cm-deletedChunk': { backgroundColor: 'var(--removed-soft) !important' },
  '&.cm-merge-b .cm-changedLine, .cm-inlineChangedLine': { backgroundColor: 'var(--added-soft) !important' },
  '&.cm-merge-a .cm-changedText, .cm-deletedChunk .cm-deletedText': { background: 'color-mix(in srgb, var(--removed) 28%, transparent) !important' },
  '&.cm-merge-b .cm-changedText': { background: 'color-mix(in srgb, var(--added) 28%, transparent) !important' },
  '&.cm-merge-a .cm-changedLineGutter, .cm-deletedLineGutter': { background: 'var(--removed) !important' },
  '&.cm-merge-b .cm-changedLineGutter': { background: 'var(--added) !important' },
  '.cm-collapsedLines': {
    color: 'var(--text-muted) !important',
    background: 'var(--panel-2) !important',
    fontFamily: 'var(--font-sans)',
    fontSize: '11.5px',
    padding: '3px 12px !important',
  },
  '.cm-collapsedLines:before, .cm-collapsedLines:after': { display: 'none' },
});

const baseExtensions = (path: string): Extension[] => [
  lineNumbers(),
  EditorState.readOnly.of(true),
  EditorView.editable.of(false),
  EditorView.contentAttributes.of({ tabindex: '0' }),
  languageFor(path),
  syntaxHighlighting(highlight),
  search({ top: true }),
  highlightSelectionMatches(),
  keymap.of([...searchKeymap, ...defaultKeymap]),
  editorTheme,
  diffTheme,
];

const COLLAPSE = { margin: 3, minSize: 4 };

function Diff({ path, before, after, layout }: { path: string; before: string; after: string; layout: DiffLayout }) {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const parent = host.current;
    if (!parent) return;
    if (layout === 'split') {
      const view = new MergeView({
        a: { doc: before, extensions: [...baseExtensions(path), EditorView.lineWrapping] },
        b: { doc: after, extensions: [...baseExtensions(path), EditorView.lineWrapping] },
        parent,
        gutter: true,
        collapseUnchanged: COLLAPSE,
        diffConfig: { timeout: 1500 },
      });
      return () => view.destroy();
    }
    const view = new EditorView({
      parent,
      state: EditorState.create({
        doc: after,
        extensions: [...baseExtensions(path), EditorView.lineWrapping, unifiedMergeView({ original: before, mergeControls: false, gutter: true, collapseUnchanged: COLLAPSE, diffConfig: { timeout: 1500 } })],
      }),
    });
    return () => view.destroy();
  }, [path, before, after, layout]);
  return <div ref={host} className="mv-diff h-full overflow-hidden" data-testid="file-diff" data-layout={layout} />;
}

function Content({ path, text }: { path: string; text: string }) {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!host.current) return;
    const view = new EditorView({ parent: host.current, state: EditorState.create({ doc: text, extensions: [...baseExtensions(path), EditorView.contentAttributes.of({ 'aria-label': `Contents of ${path}` })] }) });
    return () => view.destroy();
  }, [path, text]);
  return <div ref={host} className="h-full" data-testid="file-content" />;
}

function Message({ children }: { children: React.ReactNode }) {
  return <div className="flex h-full items-center justify-center p-6 text-center text-[13px] text-muted">{children}</div>;
}

/** Why a side of a change cannot be shown, or null when it can. */
function problem(content: FileContent): string | null {
  if (content.kind === 'binary') return `a binary file (${formatSize(content.size)})`;
  if (content.kind === 'large') return `too large to show (${formatSize(content.size)})`;
  if (content.kind === 'missing') return 'not in this clone';
  return null;
}

function ChangeView({ change, layout }: { change: FileChange; layout: DiffLayout }) {
  const [sides, setSides] = useState<{ key: string; before: FileContent; after: FileContent } | null>(null);
  const key = `${change.oldId}:${change.newId}`;
  useEffect(() => {
    let cancelled = false;
    void changeContents(change).then(([before, after]) => !cancelled && setSides({ key, before, after }));
    return () => {
      cancelled = true;
    };
  }, [change, key]);
  if (change.kind === 'submodule') return <Message>A submodule: it now points at commit {change.newId?.slice(0, 7) ?? 'nothing'}.</Message>;
  if (!sides || sides.key !== key) return <Loading />;
  const issue = problem(sides.before) ?? problem(sides.after);
  if (issue) {
    const what = change.status === 'added' ? 'Added' : change.status === 'deleted' ? 'Deleted' : 'Changed';
    return (
      <Message>
        {what}: {issue}.
      </Message>
    );
  }
  const text = (c: FileContent) => (c.kind === 'text' ? c.text : '');
  return <Diff path={change.path} before={text(sides.before)} after={text(sides.after)} layout={change.status === 'added' || change.status === 'deleted' ? 'unified' : layout} />;
}

function FileView({ commit, path }: { commit: string | null; path: string }) {
  const [content, setContent] = useState<{ key: string; value: FileContent } | null>(null);
  const key = `${commit}:${path}`;
  useEffect(() => {
    let cancelled = false;
    void fileContent(commit, path).then((value) => !cancelled && setContent({ key, value }));
    return () => {
      cancelled = true;
    };
  }, [commit, path, key]);
  if (!content || content.key !== key) return <Loading />;
  const value = content.value;
  if (value.kind === 'text') return <Content path={path} text={value.text} />;
  if (value.kind === 'missing') return <Message>{commit ? 'This file is not in this commit.' : 'This file is gone.'}</Message>;
  return <Message>This file is {problem(value)}.</Message>;
}

function Loading() {
  return (
    <div className="flex h-full items-center justify-center gap-2 text-muted">
      <Loader2 size={16} className="animate-spin" /> Reading…
    </div>
  );
}

export function FilePane({ narrow, onBack }: { narrow: boolean; onBack?: () => void }) {
  const repo = useFiles((s) => s.repo);
  const file = useFiles((s) => s.file);
  const commit = useFiles((s) => s.commit);
  const changes = useFiles((s) => s.changes);
  const history = useFiles((s) => s.history);
  const counts = useFiles((s) => (s.file ? s.counts[s.file] : undefined));
  const [chosen, setChosen] = useState<DiffLayout>(savedLayout);
  const layout = narrow ? 'unified' : chosen;
  const git = repo.state === 'ready';
  const change = file ? changes?.find((c) => c.path === file) : undefined;

  const choose = (value: DiffLayout) => {
    setChosen(value);
    try {
      localStorage.setItem(LAYOUT_KEY, value);
    } catch {
      // Kept for this visit only.
    }
  };

  const name = file ? file.slice(file.lastIndexOf('/') + 1) : '';
  const folder = file?.includes('/') ? file.slice(0, file.lastIndexOf('/')) : '';

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* No header until there is a file to name (or a way back to the files). */}
      {(file || onBack) && (
        <div className="flex h-10 flex-none items-center gap-2 border-b border-border px-2 pointer-coarse:h-12">
          {onBack && (
            <button type="button" onClick={onBack} className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-muted hover:bg-hover hover:text-text pointer-coarse:h-9 pointer-coarse:w-9" aria-label="Back to the files">
              <ArrowLeft size={15} />
            </button>
          )}
          {file ? (
            <p className="min-w-0 flex-1 truncate text-[12.5px]" title={change?.oldPath ? `${file} (renamed from ${change.oldPath})` : file} data-testid="file-title">
              {folder && <span className="text-faint">{folder}/</span>}
              <span className={cn('font-medium', change ? STATUS_COLOR[change.status] : 'text-text')}>{name}</span>
              {change?.oldPath && <span className="ml-1.5 text-faint">from {change.oldPath}</span>}
              {counts && counts !== 'binary' && (
                <span className="ml-2 font-mono text-[11px]">
                  <span className="text-added">+{counts.added}</span> <span className="text-removed">−{counts.removed}</span>
                </span>
              )}
            </p>
          ) : (
            <span className="flex-1" />
          )}
          {change && change.status !== 'added' && change.status !== 'deleted' && !narrow && (
            <div role="radiogroup" aria-label="Diff layout" className="flex flex-none rounded-md border border-border p-0.5">
              {(
                [
                  ['split', 'Side by side', Columns2],
                  ['unified', 'One column', Rows3],
                ] as const
              ).map(([value, label, Icon]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={layout === value}
                  aria-label={label}
                  title={label}
                  onClick={() => choose(value)}
                  className={cn('flex h-6 w-7 items-center justify-center rounded', layout === value ? 'bg-active text-text' : 'text-muted hover:text-text')}
                >
                  <Icon size={13} />
                </button>
              ))}
            </div>
          )}
          {git && file && (
            <button
              type="button"
              onClick={() => void showHistory(history === file ? null : file)}
              aria-pressed={history === file}
              className={cn(
                'flex h-7 flex-none items-center gap-1.5 rounded-md px-2 text-[12px] pointer-coarse:h-9',
                history === file ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-hover hover:text-text',
              )}
              title={history === file ? 'Show every commit again' : 'List only the commits that changed this file'}
            >
              <History size={13} /> History
            </button>
          )}
        </div>
      )}
      <div className="min-h-0 flex-1">
        {!file ? (
          <Message>{git && changes?.length === 0 ? 'This commit changed no files.' : 'Pick a file.'}</Message>
        ) : change ? (
          <ChangeView key={change.path} change={change} layout={layout} />
        ) : (
          <FileView commit={git ? commit : null} path={file} />
        )}
      </div>
    </div>
  );
}
